/** Reine Helfer fuer die Laufhistorie (ohne React und ohne api), damit sie testbar bleiben. */
import { laufStatusAnzeige, mailsGesendet, mailStatusAnzeige, type Anzeige } from './lauf';
import type { BleibtEintrag, HistorieEintrag, LieferantErgebnis, ProtokollLauf, SchreibErgebnis } from './typen';

const GRUND_REIHENFOLGE: Record<string, number> = {
  'Lieferauftrag vorhanden': 0,
  'Artikeldaten fehlen': 1,
  'kein Lieferant': 2,
  'nicht in der bestätigten Vorschau — heute nicht bestellt, nächster Lauf': 4,
  // Alter Wortlaut bleibt für frühere Protokolle.
  'kein Testauftrag (Kunden-Mail nicht @anstoss24.de) — wird vom Testlauf nicht angefasst': 3,
  'kein Testauftrag (Kunden-Mail keine freigegebene Testadresse @anstoss24.de) — wird vom Testlauf nicht angefasst': 3,
  'Testauftrag nicht im Startdialog bestätigt — wird vom Testlauf nicht angefasst': 4,
};

export function zeitText(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', { timeZone: 'Europe/Berlin', dateStyle: 'short', timeStyle: 'short' });
}

export interface HistorieZeile {
  id: number;
  zeit: string;
  referenz: string;
  status: Anzeige;
  /** WR-05: Testmodus oder Testlauf (Mails an die Testadresse) — in der Liste als „TEST" markiert. */
  testmodus: boolean;
  /** Testlauf (test-3.4): Plenty-Write nur für Testaufträge; eigener Tooltip am „TEST"-Badge. */
  testlauf: boolean;
  gestartetVon: string;
  lieferanten: Array<{ name: string; mailStatus: Anzeige; positionen: number }>;
}

export function historieZeile(eintrag: HistorieEintrag): HistorieZeile {
  const lieferanten = (eintrag.ergebnis?.lieferanten ?? [])
    .filter((l) => l.positionen > 0 || l.fehler)
    .map((l) => ({ name: l.name, mailStatus: mailStatusAnzeige(l.mailStatus), positionen: l.positionen }));
  return {
    id: eintrag.id,
    zeit: zeitText(eintrag.gestartetAm),
    referenz: eintrag.referenz ?? '—',
    status: laufStatusAnzeige(eintrag.status),
    testmodus: eintrag.ergebnis?.testmodus === true || eintrag.ergebnis?.testlauf === true,
    testlauf: eintrag.ergebnis?.testlauf === true,
    gestartetVon: eintrag.gestartetVon ?? '—',
    lieferanten,
  };
}

/** Eine Zeile je Konversation: Auftrag → Lieferant → FreeScout-Nummer (WR-05). Pure. */
export function konversationsZeilen(
  konversationen: DetailAbschnitte['konversationen'],
  namen: Record<string, string> = {},
): string[] {
  return konversationen.map((k) => {
    const lieferant = namen[k.lieferantId] ?? k.lieferantId;
    const fs =
      k.conversationId == null
        ? 'keine Konversations-ID — in FreeScout (info@) suchen'
        : k.nummer != null
          ? `FreeScout #${k.nummer} (Konversation ${k.conversationId})`
          : `FreeScout Konversation ${k.conversationId}`;
    return `Auftrag ${k.orderId}: ${lieferant} — ${fs}`;
  });
}

export interface DetailAbschnitte {
  lieferanten: LieferantErgebnis[];
  auftraegeVersendet: number[];
  auftraegeMitFehlschlag: number[];
  /** Aufträge mit unklarem Versand (CR-01); leer bei Altläufen. */
  auftraegeVersandUnklar: number[];
  bleibtInStatus: BleibtEintrag[];
  /** undefined = Lauf vor Phase 4 (Feld fehlt), null = abgebrochen vor dem Schreiben (WR-06). */
  schreiben: SchreibErgebnis | null | undefined;
  /** Anzahl versendeter Mails, für die Warnung bei abgebrochenen Läufen. */
  mailsGesendet: number;
  dateien: Array<{ lieferantId: string; lieferantName: string; dateiname: string }>;
  /** Testmodus-Lauf oder Testlauf (Altläufe: false). */
  testmodus: boolean;
  /** Testlauf (test-3.4). */
  testlauf: boolean;
  /** Status, in dem nicht bestellte Aufträge bleiben (Altläufe: Testlauf 3.4, sonst 5.2). */
  quellStatus: number;
  /** FreeScout-Konversationen je Auftrag, nach Auftrag sortiert; leer bei Altläufen. */
  konversationen: Array<{ orderId: number; lieferantId: string; conversationId: number | null; nummer: number | null }>;
}

export function detailAbschnitte(lauf: ProtokollLauf): DetailAbschnitte {
  const e = lauf.ergebnis;
  const namen = new Map((e?.lieferanten ?? []).map((l) => [l.id, l.name]));
  return {
    lieferanten: e?.lieferanten ?? [],
    auftraegeVersendet: e?.auftraegeKomplettVersendet ?? [],
    auftraegeMitFehlschlag: e?.auftraegeMitFehlschlag ?? [],
    auftraegeVersandUnklar: e?.auftraegeVersandUnklar ?? [],
    schreiben: e && 'schreiben' in e ? (e.schreiben ?? null) : undefined,
    mailsGesendet: mailsGesendet(e),
    testmodus: e?.testmodus === true || e?.testlauf === true,
    testlauf: e?.testlauf === true,
    quellStatus: e?.quellStatus ?? (e?.testlauf === true ? 3.4 : 5.2),
    konversationen: Object.entries(e?.konversationenJeAuftrag ?? {})
      .flatMap(([orderId, liste]) => (Array.isArray(liste) ? liste : []).map((k) => ({ orderId: Number(orderId), ...k })))
      .sort((a, b) => a.orderId - b.orderId || a.lieferantId.localeCompare(b.lieferantId)),
    bleibtInStatus: [...(e?.bleibtInStatus ?? [])].sort(
      (x, y) => (GRUND_REIHENFOLGE[x.grund] ?? 9) - (GRUND_REIHENFOLGE[y.grund] ?? 9) || x.orderId - y.orderId,
    ),
    dateien: lauf.dateiListe.map((d) => ({
      lieferantId: d.lieferantId,
      lieferantName: namen.get(d.lieferantId) ?? d.lieferantId,
      dateiname: d.dateiname,
    })),
  };
}
